/* ------------------------ Functional imports start ------------------------ */
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { useImageEditorContext } from './ImageEditorContext'
import type Konva from 'konva'
import {
  buildVirtualStage,
  isCircleConfig,
  isEllipseConfig,
  isRectangleConfig,
} from './utils/functions'
import LogTool from '../../logger/logTools'
import type { KonvaEventObject, Node, NodeConfig } from 'konva/lib/Node'
/* ------------------------- Functional imports end ------------------------- */

/* -------------------------- Design imports start -------------------------- */
import ZoomableDiv from '../layout/ZoomableDiv'
import { Stage, Layer, Image, Rect, Circle, Transformer, Ellipse } from 'react-konva'
import { hexToRgba } from '@uiw/react-color'
import { Box } from '@mui/material'
import { ImageEditorExportSettings } from './utils/types'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
/* --------------------------- Design imports end --------------------------- */

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function ImageEditorWorkspace(): ReactNode {
  /* ------------------------- Non state data start ------------------------- */
  const { image, shapes, shapeSelection, updateShape, previewSettings, exportCanvas } =
    useImageEditorContext()
  const log = new LogTool({ context: 'ImageEditorWorkspace', logLevel: 'debug', enable: true })
  const { t } = useTranslation()
  /* -------------------------- Non state data end -------------------------- */

  /* --------------------------- Flag states start -------------------------- */
  // store whether the user is currently clicking on (interacting with) a shape.
  // We need to keep track of that to make sure that the panning of the workspace
  // does not conflict with the shape interactions.
  const [activeShapeInteraction, setActiveShapeInteraction] = useState<boolean>(false)
  /* ---------------------------- Flag states end --------------------------- */

  /* --------------------------- Data states start -------------------------- */
  // keep a ref to the Stages container element to enable dynamic stage sizing
  const canvasStageContainerRef = useRef<HTMLElement | null>(null)
  const [stageDimensions, setStageDimensions] = useState<
    { width: number; height: number } | undefined
  >(undefined)
  const canvasStageRef = useRef<Konva.Stage | null>(null)
  // storing a ref to the Konva.Image instance enables us to direct Konva to cache
  // the loaded image. This ensures maximum performance even for large images!
  const canvasImageRef = useRef<Konva.Image | null>(null)
  const canvasNodeRefs = useRef<Map<string, Konva.Node>>(new Map())
  const transformerRef = useRef<Konva.Transformer | null>(null)
  /* ---------------------------- Data states end --------------------------- */

  /* ----------------------------- Effects start ---------------------------- */
  // register export canvas function on mount
  useEffect(() => {
    exportCanvas.current = (settings: ImageEditorExportSettings) => {
      if (!canvasStageRef.current) throw new Error('Canvas stage not mounted yet.')
      // 1. Prepare a hidden Konva stage that is an exact replica of the one rendered on screen
      const virtualKonvaStage = buildVirtualStage(settings.image.bitmap!, settings.shapes)
      // 2. Render the stage and export the image
      virtualKonvaStage.draw()
      virtualKonvaStage
        .toBlob({
          callback: (exportedImage: Blob | null) => {
            if (exportedImage) settings.onImageAvailable(exportedImage)
            else {
              log.error('Error exporting virtual canvas: image not available.')
              toast.error(t('imageManipulation:feedback.error.exportingImage'))
            }
          },
        })
        .catch(reason => {
          log.error('Error exporting virtual canvas: ', reason)
          toast.error(t('imageManipulation:feedback.error.exportingImage'))
        })
      // 4. Cleanup - Important to avoid memory leaks
      virtualKonvaStage.destroy()
    }
  }, [])

  // update Konva stage size on mount and window resize
  useEffect(() => {
    // update stage size on mount
    updateStageSize()
    // ... and every time the window resizes
    window.addEventListener('resize', updateStageSize)
    // remove event listener on un-mount
    return () => {
      window.removeEventListener('resize', updateStageSize)
    }
  }, [])

  // Cache the image loaded into the Konva.Image component to maximize performance
  // and center the image in the container.
  // The cache is refresehd, everytime the input image (image.bitmap) changes.
  useEffect(() => {
    if (canvasImageRef.current && image.bitmap) {
      // 1. Cache the new bitmap
      canvasImageRef.current.cache()
      // 2. Rerender the layer to enforce using the cache
      canvasImageRef.current.getLayer()?.batchDraw()

      // 3. Center the image on the stage
      if (canvasStageRef.current && canvasStageContainerRef.current) {
        const containerWidth = canvasStageContainerRef.current.offsetWidth
        const containerHeight = canvasStageContainerRef.current.offsetHeight

        // calculate the scale of the stage so that the image is fully visible.
        // To do so, we have to make sure that that the longer side of the image
        // fits into the visible area.
        const scale = Math.min(containerWidth / image.width!, containerHeight / image.height!)

        // calculate the required offset to center the image within the visible area
        const posX = (containerWidth - image.width! * scale) / 2
        const posY = (containerHeight - image.height! * scale) / 2

        // update stage scale and position to center the image
        const stage = canvasStageRef.current
        stage.scale({ x: scale, y: scale })
        stage.position({ x: posX, y: posY })
      }
    }
  }, [image.bitmap])

  // Update transformer when selection changes
  useEffect(() => {
    if (shapeSelection.ids.length && transformerRef.current) {
      // Get the selected canvas nodes
      const nodes = shapeSelection.ids
        .map(id => canvasNodeRefs.current.get(id))
        .filter(node => typeof node !== 'undefined')

      transformerRef.current.nodes(nodes)
    } else if (transformerRef.current) {
      // Clear selection
      transformerRef.current.nodes([])
    }
  }, [shapeSelection.ids])
  /* ------------------------------ Effects end ----------------------------- */

  /* ------------------------ Utility functions start ----------------------- */
  const updateStageSize = useCallback(() => {
    if (!canvasStageContainerRef.current) return
    // update stage width and height
    const container = canvasStageContainerRef.current
    setStageDimensions({
      width: container.offsetWidth,
      height: container.offsetHeight,
    })
  }, [canvasStageContainerRef.current, setStageDimensions])
  /* ------------------------- Utility functions end ------------------------ */

  /* ----------------------- Callback functions start ----------------------- */
  /**
   * Triggered on click events that occur on the stage element (either on empty
   * space or on a shape).
   * @param e Mouse click event.
   */
  const handleStageClick = (e: Konva.KonvaEventObject<MouseEvent>): void => {
    if (e.target === e.target.getStage() || 'image' in e.target.attrs) {
      // the user clicked on an empty area - remove all selections
      shapeSelection.setIds([])
      // ... and enable panning (again)
      setActiveShapeInteraction(false)
    }
  }
  /**
   * Callback that uses the scale and position properties of the Konva stage element
   * to enable zooming the stage based on the mouse pointer position.
   * @param e Mousewheel event
   */
  const handleStageZoom = (e: Konva.KonvaEventObject<WheelEvent>): void => {
    e.evt.preventDefault()
    if (!canvasStageRef.current) return
    const stage = canvasStageRef.current
    const pointer = stage.getPointerPosition()

    // zoom settings
    // how to scale? In or out?
    const isTrackpadZoom = e.evt.deltaY % 1 !== 0 || Math.abs(e.evt.deltaY) < 50 // heuristic trying to differentiate between trackpad and mouse
    let direction = e.evt.deltaY > 0 ? 1 : -1
    if (e.evt.ctrlKey) direction = -direction
    if (!isTrackpadZoom) direction = -direction
    const scaleBy = 1.04 // this value controlls the zoom speed

    // calculate an updated scale value that results in the stage being zoomed in or our
    const previousScale = stage.scaleX()
    const updatedScale = direction > 0 ? previousScale * scaleBy : previousScale / scaleBy

    // update stage to render zoom-effect
    stage.scale({ x: updatedScale, y: updatedScale })

    // if we have access to the pointer, we can scale / zoom the canvas stage centered
    // arount the pointers position
    if (pointer !== null) {
      const mousePointsTo = {
        x: (pointer.x - stage.x()) / previousScale,
        y: (pointer.y - stage.y()) / previousScale,
      }
      const updatedStagePosition = {
        x: pointer.x - mousePointsTo.x * updatedScale,
        y: pointer.y - mousePointsTo.y * updatedScale,
      }
      // update stage position
      stage.position(updatedStagePosition)
    }
  }

  /**
   * Handles click events that occur on the canvas node of a shape.
   * @param e The click event
   * @param shape The configuration object of the shape that was clicked on.
   */
  const handleShapeClick = (
    e: Konva.KonvaEventObject<MouseEvent>,
    shape: Konva.ShapeConfig
  ): void => {
    // register shape interaction
    setActiveShapeInteraction(true)

    // --- Handle selection ---
    if (shape.id === undefined) throw new Error('Every shape must have a unique id.')
    // Did the user press shift or ctrl?
    const metaPressed = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey
    const isSelected = shapeSelection.ids.includes(shape.id)

    if (!metaPressed && !isSelected) {
      // No key was pressed, and the node is not selected -> select just the node
      shapeSelection.setIds([shape.id])
    } else if (metaPressed && isSelected) {
      // Ctrl or shift was pressed, and the node is selected already
      // -> remove it from selection
      shapeSelection.setIds(prev => prev.filter(id => id !== shape.id))
    } else if (metaPressed && !isSelected) {
      // Add the node to the selection
      shapeSelection.setIds(prev => [...prev, shape.id as string])
    }
  }
  /**
   * Handles releasing a click on the canvas node of a shape.
   */
  const handleShapeRelease = (): void => {
    // re-enable panning
    setActiveShapeInteraction(false)
  }

  const handleShapeDragEnd = (e: Konva.KonvaEventObject<DragEvent>): void => {
    updateShape(e.target.id(), {
      x: e.target.x(),
      y: e.target.y(),
    })
  }

  /**
   * Handles the end of a canvas node transformation: Applies the transformation
   * to the shape configuration object and updates the state.
   * @param e Generic Konva event that is fired when the transformation ends.
   */
  const handleShapeTransformEnd = (e: Konva.KonvaEventObject<Event>): void => {
    const shapeId = e.target.id()

    // cache scale values before resetting them
    const cachedScaleX = e.target.scaleX()
    const cachedScaleY = e.target.scaleY()

    // Reset scale
    e.target.scaleX(1)
    e.target.scaleY(1)

    // Update
    updateShape(shapeId, {
      x: e.target.x(),
      y: e.target.y(),
      rotation: e.target.rotation(),
      ...(isRectangleConfig(e.target.attrs as Konva.ShapeConfig) && {
        width: Math.max(5, e.target.width() * cachedScaleX),
        height: Math.max(e.target.height() * cachedScaleY),
      }),
      ...(isEllipseConfig(e.target.attrs as Konva.ShapeConfig) && {
        radiusX: Math.max(5, e.target.attrs.radiusX * cachedScaleX),
        radiusY: Math.max(e.target.attrs.radiusY * cachedScaleY),
      }),
    })
  }

  /* ------------------------ Callback functions end ------------------------ */

  /* ------------------------ Render constants start ------------------------ */
  const shapeConfigs: Konva.ShapeConfig[] = shapes ? Object.values(shapes) : []
  let opacity = 1 // solid opacity
  if (previewSettings.mode === 'xray') opacity = 0.75
  if (previewSettings.mode === 'outline') opacity = 0
  /* ------------------------- Render constants end ------------------------- */

  /* ----------------------- Pre render actions start ----------------------- */

  /* ------------------------ Pre render actions end ------------------------ */

  /* ------------------------------------------------------------------------ */
  /*                             Render Component                             */
  /* ------------------------------------------------------------------------ */
  return (
    <Box
      ref={canvasStageContainerRef}
      width="100%"
      height="100%"
      boxShadow="0 0 4px 2px rgba(0,0,0,0.03)"
      bgcolor="whitesmoke"
      borderRadius="4px"
      // we need to register the mouse leave handler on this wrapper element
      // as react-konva won't fire the mouse event as long as the mouse 'belongs'
      // to a shape due to dragging
      // onMouseLeave={handleStageMouseLeave}
    >
      <Stage
        ref={canvasStageRef}
        width={stageDimensions ? stageDimensions.width : 0}
        height={stageDimensions ? stageDimensions.height : 0}
        onClick={handleStageClick}
        onWheel={handleStageZoom}
        draggable
      >
        <Layer>
          {image && (
            <Image
              ref={canvasImageRef}
              image={image.bitmap}
              width={image.width}
              height={image.height}
              // perfectDrawEnabled={false} // disable edge-rendering-optimization to gain performance
            />
          )}
          {shapeConfigs &&
            shapeConfigs.map(shape => {
              // prepare color
              const shapeColor = { ...hexToRgba(shape.fill as string), a: opacity }
              // add all properties required to make the shape interactable
              const interactiveShapeConfig: Konva.ShapeConfig = {
                ...shape,
                ref: (node: never) => {
                  if (node) canvasNodeRefs.current.set(shape.id as string, node)
                },
                onMouseDown: (e: KonvaEventObject<MouseEvent, Node<NodeConfig>>): void =>
                  handleShapeClick(e, shape),
                onMouseUp: handleShapeRelease,
                onDragEnd: handleShapeDragEnd,
                onTransformEnd: handleShapeTransformEnd,

                // preview settings
                fill: `rgba(${shapeColor.r}, ${shapeColor.g}, ${shapeColor.b}, ${shapeColor.a})`,
                stroke: '#16a9e4',
                strokeWidth: 1,
                strokeEnabled:
                  previewSettings.mode === 'xray' || previewSettings.mode === 'outline',
              }

              // render the shape
              if (isRectangleConfig(interactiveShapeConfig)) {
                return <Rect key={shape.id} {...interactiveShapeConfig} />
              }
              if (isCircleConfig(interactiveShapeConfig)) {
                return <Circle key={shape.id} {...interactiveShapeConfig} />
              }
              if (isEllipseConfig(interactiveShapeConfig)) {
                return <Ellipse key={shape.id} {...interactiveShapeConfig} />
              }

              // none of the above shape types matched -> nothing to render
              throw new Error(`Shape type of shape ${shape.id} not supported currently.`)
            })}

          {/* Single transformer for all selected shapes */}
          <Transformer
            ref={transformerRef}
            boundBoxFunc={(oldBox, newBox) => {
              // Limit resize
              if (newBox.width < 5 || newBox.height < 5) {
                return oldBox
              }
              return newBox
            }}
            keepRatio={false}
            onTransformStart={() => setActiveShapeInteraction(true)}
            onTransformEnd={() => setActiveShapeInteraction(false)}
            onMouseDown={() => setActiveShapeInteraction(true)}
            onMouseUp={() => setActiveShapeInteraction(false)}
            // styling
            borderStrokeWidth={1}
            borderStroke={'gray'}
          />
        </Layer>
      </Stage>
    </Box>
  )
}
